import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { ArrowBack, Delete as DeleteIcon, Edit as EditIcon, Save, Cancel } from '@mui/icons-material';
import { customerPaymentService } from '../services/customerPaymentService';
import { formatCurrency } from '../components/CurrencyTextField';
import ApplyPaymentAllocationsDialog from '../components/ApplyPaymentAllocationsDialog';
import { formatDateOnly } from '../utils/dateUtils';

const PaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingReference, setEditingReference] = useState(false);
  const [editReferenceNumber, setEditReferenceNumber] = useState('');
  const [savingReference, setSavingReference] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);

  useEffect(() => {
    if (id) loadPayment();
  }, [id]);

  const loadPayment = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await customerPaymentService.getById(id);
      setPayment(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => formatDateOnly(d, 'MM/dd/yyyy') || '—';
  const customerName = payment?.company_name ||
    [payment?.customer_first_name, payment?.customer_last_name].filter(Boolean).join(' ') || '—';

  const recordedByName =
    [payment?.recorded_by_first_name, payment?.recorded_by_last_name].filter(Boolean).join(' ').trim() || '—';

  const orderDetailId = payment
    ? payment.resolved_work_order_id ?? payment.work_order_id
    : null;
  const orderDetailLabel = payment
    ? payment.resolved_work_order_label ||
      payment.work_order_identifier ||
      payment.work_order_number ||
      (orderDetailId != null ? `#${orderDetailId}` : null)
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!payment) {
    return (
      <Box>
        <Alert severity="error">Payment not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customer-payments')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  const totalAllocated = parseFloat(payment.amount_allocated ?? 0) ||
    (payment.allocations || []).reduce((s, a) => s + parseFloat(a.amount_applied || 0), 0);
  const amountUnapplied = parseFloat(payment.amount_unapplied ?? 0) ||
    Math.max(0, Math.round((parseFloat(payment.amount || 0) - totalAllocated) * 100) / 100);
  const canApplyMore = amountUnapplied > 0.01 && payment.payment_method !== 'Customer Credit';

  const startEditReference = () => {
    setEditReferenceNumber(payment?.reference_number || '');
    setEditingReference(true);
  };

  const handleSaveReference = async () => {
    if (!payment?.id) return;
    try {
      setSavingReference(true);
      setError('');
      await customerPaymentService.update(payment.id, {
        reference_number: String(editReferenceNumber || '').trim() || null
      });
      setEditingReference(false);
      await loadPayment();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payment reference');
    } finally {
      setSavingReference(false);
    }
  };

  const startEditNotes = () => {
    setEditNotes(payment?.notes || '');
    setEditingNotes(true);
  };

  const handleSaveNotes = async () => {
    if (!payment?.id) return;
    try {
      setSavingNotes(true);
      setError('');
      await customerPaymentService.update(payment.id, {
        notes: String(editNotes || '').trim() || null
      });
      setEditingNotes(false);
      await loadPayment();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payment notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      setError('');
      await customerPaymentService.delete(payment.id);
      setDeleteDialogOpen(false);
      navigate('/customer-payments');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete payment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/customer-payments')} title="Back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600, flex: 1 }}>
          Payment {payment.payment_number}
        </Typography>
        <Button
          color="error"
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete payment
        </Button>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {payment.payment_number} ({formatCurrency(payment.amount)}) will be permanently removed.
            {(payment.allocations || []).length > 0
              ? ' Invoice allocations will be reversed and invoice balances updated.'
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Customer</Typography>
              <Typography variant="body2">
                {payment.customer_id ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    onClick={() => navigate(`/customers/${payment.customer_id}`)}
                  >
                    {customerName}
                  </Button>
                ) : (
                  customerName
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Order</Typography>
              <Typography variant="body2">
                {orderDetailId != null ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    onClick={() => navigate(`/work-orders/${orderDetailId}`)}
                  >
                    {orderDetailLabel || `Order #${orderDetailId}`}
                  </Button>
                ) : (
                  '—'
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body2">{formatDate(payment.payment_date)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Amount</Typography>
              <Typography variant="body2">{formatCurrency(payment.amount)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Method</Typography>
              <Typography variant="body2">{payment.payment_method || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Recorded by</Typography>
              <Typography variant="body2">{recordedByName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Reference</Typography>
              {editingReference ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                  <TextField
                    size="small"
                    value={editReferenceNumber}
                    onChange={(e) => setEditReferenceNumber(e.target.value)}
                    inputProps={{ maxLength: 100, 'aria-label': 'Payment reference number' }}
                    sx={{ width: 220, maxWidth: '100%' }}
                  />
                  <IconButton size="small" color="primary" onClick={handleSaveReference} disabled={savingReference} title="Save reference">
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingReference(false)} disabled={savingReference} title="Cancel">
                    <Cancel fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">{payment.reference_number || '—'}</Typography>
                  <IconButton size="small" onClick={startEditReference} title="Edit reference">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                {!editingNotes ? (
                  <IconButton size="small" onClick={startEditNotes} title="Edit notes" sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                ) : null}
              </Box>
              {editingNotes ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.25 }}>
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    fullWidth
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    inputProps={{ 'aria-label': 'Payment notes' }}
                  />
                  <IconButton size="small" color="primary" onClick={handleSaveNotes} disabled={savingNotes} title="Save notes">
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingNotes(false)} disabled={savingNotes} title="Cancel">
                    <Cancel fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}
                >
                  {payment.notes?.trim() ? payment.notes : '—'}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Applied to Invoices</Typography>
            {canApplyMore && (
              <Button size="small" variant="contained" onClick={() => setApplyDialogOpen(true)}>
                Apply {formatCurrency(amountUnapplied)} to invoices
              </Button>
            )}
          </Box>
          {(payment.allocations || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No allocations</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'surfaceMuted.main' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount Applied</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(payment.allocations || []).map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate(`/customer-invoices/${a.customer_invoice_id}`)}
                        >
                          {a.invoice_identifier || a.identifier || a.invoice_number || `#${a.customer_invoice_id}`}
                        </Button>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(a.amount_applied)}</TableCell>
                      <TableCell align="right">—</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2">
              Total applied: {formatCurrency(totalAllocated)} / {formatCurrency(payment.amount)}
            </Typography>
            {amountUnapplied > 0.01 && (
              <Typography variant="body2" color="warning.main">
                Unapplied on this payment (included in customer credit): {formatCurrency(amountUnapplied)}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <ApplyPaymentAllocationsDialog
        open={applyDialogOpen}
        onClose={() => setApplyDialogOpen(false)}
        onSuccess={loadPayment}
        payment={payment}
        maxAmount={amountUnapplied}
      />
    </Box>
  );
};

export default PaymentDetail;
